import { supabase } from '../lib/supabase';

export const listingService = {
  // Create a new listing
  async createListing(listing) {
    try {
      // Build filters JSONB for search optimization
      const filters = {
        ...(listing.filters || {}), // Include any filters passed in (like dates for gigs)
        location: listing.location,
        traveling: listing.traveling,
        remote: listing.remote,
        industry: listing.industry,
        role: listing.role,
        skills: listing.skills,
        age: listing.age,
        size: listing.size,
        time_commitment: listing.time_commitment,
        pay: listing.pay
      };

      const { data, error } = await supabase
        .from('listings')
        .insert({
          ...listing,
          filters
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create listing error:', error);
      throw error;
    }
  },

  // Update a listing
  async updateListing(listingId, updates) {
    try {
      // Update filters if relevant fields are being updated
      if (updates.location || updates.traveling || updates.remote || 
          updates.industry || updates.role || updates.skills || 
          updates.age || updates.size || updates.time_commitment || updates.pay) {
        
        // Get current listing to merge filters
        const { data: currentListing } = await supabase
          .from('listings')
          .select('filters')
          .eq('id', listingId)
          .single();

        const currentFilters = currentListing?.filters || {};
        
        updates.filters = {
          ...currentFilters,
          location: updates.location || currentFilters.location,
          traveling: updates.traveling !== undefined ? updates.traveling : currentFilters.traveling,
          remote: updates.remote || currentFilters.remote,
          industry: updates.industry || currentFilters.industry,
          role: updates.role || currentFilters.role,
          skills: updates.skills || currentFilters.skills,
          age: updates.age || currentFilters.age,
          size: updates.size || currentFilters.size,
          time_commitment: updates.time_commitment || currentFilters.time_commitment,
          pay: updates.pay || currentFilters.pay
        };
      }

      const { data, error } = await supabase
        .from('listings')
        .update(updates)
        .eq('id', listingId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update listing error:', error);
      throw error;
    }
  },

  // Get active listings with filters
  async getListings(filters = {}, limit = 20, offset = 0) {
    try {
      let query = supabase
        .from('listings')
        .select(`
          *,
          profiles:public_profiles!owner_id (
            display_name,
            location
          )
        `)
        .eq('status', 'active')
        .or('expires_at.is.null,expires_at.gte.' + new Date().toISOString()) // Only show non-expired listings
        .range(offset, offset + limit - 1)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.type) {
        query = query.eq('type', filters.type);
      }

      if (filters.industry) {
        query = query.eq('industry', filters.industry);
      }

      if (filters.remote) {
        query = query.eq('remote', filters.remote);
      }

      if (filters.role) {
        query = query.eq('role', filters.role);
      }

      if (filters.skills && filters.skills.length > 0) {
        query = query.overlaps('skills', filters.skills);
      }

      // Support both old (city) and new (province/area) location filtering
      if (filters.location) {
        query = query.contains('location', { city: filters.location });
      }
      
      if (filters.province) {
        query = query.contains('location', { province: filters.province });
      }
      
      if (filters.area) {
        query = query.contains('location', { area: filters.area });
      }

      const { data, error, count } = await query;

      if (error) throw error;
      return { listings: data, count };
    } catch (error) {
      console.error('Get listings error:', error);
      throw error;
    }
  },

  // Get listings by user
  async getUserListings(userId, limit = 20, offset = 0) {
    try {
      const { data, error, count } = await supabase
        .from('listings')
        .select(`
          *,
          moderation_decisions (
            status,
            reason,
            decided_at
          )
        `, { count: 'exact' })
        .eq('owner_id', userId)
        .range(offset, offset + limit - 1)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Extract the most recent rejection reason if listing is rejected
      const listingsWithRejection = data?.map(listing => {
        const rejectionDecision = listing.moderation_decisions?.find(
          decision => decision.status === 'rejected'
        );
        return {
          ...listing,
          rejection_reason: rejectionDecision?.reason || null,
          rejected_at: rejectionDecision?.decided_at || null
        };
      });
      
      return { listings: listingsWithRejection, count };
    } catch (error) {
      console.error('Get user listings error:', error);
      throw error;
    }
  },

  // Get single listing
  async getListing(listingId) {
    try {
      const { data, error } = await supabase
        .from('listings')
        .select(`
          *,
          profiles:public_profiles!owner_id (
            display_name,
            bio,
            location
          )
        `)
        .eq('id', listingId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get listing error:', error);
      throw error;
    }
  },

  // Submit listing for moderation
  async submitForModeration(listingId) {
    try {
      const { data, error } = await supabase
        .from('listings')
        .update({
          status: 'pending_moderation',
          submitted_at: new Date().toISOString()
        })
        .eq('id', listingId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Submit for moderation error:', error);
      throw error;
    }
  },

  // Delete listing
  async deleteListing(listingId) {
    try {
      const { error } = await supabase
        .from('listings')
        .delete()
        .eq('id', listingId);

      if (error) throw error;
    } catch (error) {
      console.error('Delete listing error:', error);
      throw error;
    }
  },

  // Search listings (with full-text search)
  async searchListings(searchQuery, filters = {}, limit = 20, offset = 0) {
    try {
      let query = supabase
        .from('listings')
        .select(`
          *,
          profiles:public_profiles!owner_id (
            display_name,
            location
          )
        `)
        .eq('status', 'active')
        .or('expires_at.is.null,expires_at.gte.' + new Date().toISOString()); // Only show non-expired listings

      // Add text search if provided
      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,full_story.ilike.%${searchQuery}%`);
      }

      // Apply additional filters
      if (filters.type) {
        query = query.eq('type', filters.type);
      }

      if (filters.industry) {
        query = query.eq('industry', filters.industry);
      }

      // Add pagination and ordering
      query = query
        .range(offset, offset + limit - 1)
        .order('created_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;
      return { listings: data, count };
    } catch (error) {
      console.error('Search listings error:', error);
      throw error;
    }
  },

  // Archive expired listings (call this periodically or on-demand)
  async archiveExpiredListings() {
    try {
      const { data, error } = await supabase.rpc('archive_expired_listings');
      if (error) throw error;
      return data; // Returns count of archived listings
    } catch (error) {
      console.error('Archive expired listings error:', error);
      throw error;
    }
  },

  // Check if a listing is expired or get days until expiry
  getExpiryInfo(listing) {
    if (!listing.expires_at) {
      return { isExpired: false, daysRemaining: null };
    }

    const expiryDate = new Date(listing.expires_at);
    const now = new Date();
    const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    return {
      isExpired: daysRemaining <= 0,
      daysRemaining: Math.max(0, daysRemaining),
      expiryDate,
      isExpiringSoon: daysRemaining > 0 && daysRemaining <= 7 // Within 7 days
    };
  }
};
